import { keyframes, styled } from "goober";
import { useEffect, useRef, useState } from "react";
import { queries } from "../../shared/queries";
import { useExposeWorkflowAsConnector } from "../hooks/useExposeWorkflowAsConnector";
import { useHasAccess } from "../hooks/useHasAccess";
import { useHasExposedConnectorRoutes } from "../hooks/useHasExposedConnectorRoutes";
import { useQuery } from "../hooks/useQuery";
import { navigate, routes, useCurrentUrl } from "../lib/urls";
import { playPop } from "../lib/sounds";
import { CircleMesh } from "./CircleMesh";
import { parseBadgeConfig } from "./CircleMeshEditor";
import { ClickButton } from "./ClickButton";
import { Icon } from "./Icon";
import { Tooltip } from "./Tooltip";

const flow = keyframes`
  to { transform: translate3d(3px, 0, 0); }
`;

const PillButton = styled(ClickButton)`
  position: relative;
  display: flex;
  align-items: center;
  gap: 4px;
  justify-content: center;
  height: 32px;
  padding: 4px;
  outline: 0.5px solid var(--border-alpha);
  border-radius: 9999px;
  background: transparent;
  color: var(--text-primary);
  box-sizing: border-box;
  &:hover {
    background: var(--alpha4);
  }
  &:active {
    background: var(--alpha8);
  }
  &:focus {
    outline: none;
  }
  &:focus-visible {
    outline: 2px solid var(--control-bg);
    outline-offset: 2px;
  }
`;

const IconSlot = styled("span")`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  border-radius: 9999px;
  overflow: hidden;
`;

const FlowLine = styled("span")`
  position: relative;
  width: 16px;
  height: 1px;
  flex-shrink: 0;
  overflow: hidden;
  opacity: var(--wf-app-flow-opacity, 1);

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -3px;
    width: calc(100% + 6px);
    height: 1px;
    background-image: repeating-linear-gradient(
      to right,
      var(--text-primary) 0 1.5px,
      transparent 1.5px 3px
    );
    animation: ${flow} 0.8s linear infinite;
  }
`;

type Props = {
  workflowId: string;
  spaceId: string;
  workflowName: string;
  workflowBadgeSeed: string | null;
};

function WorkflowAppBadge({
  id,
  badgeSeed,
  muted = false,
}: {
  id: string;
  badgeSeed: string | null;
  muted?: boolean;
}) {
  const badgeConfig = parseBadgeConfig(badgeSeed, id);
  return (
    <span
      style={{
        width: 16,
        height: 16,
        flexShrink: 0,
        borderRadius: 4,
        overflow: "hidden",
        ...(muted ? { filter: "grayscale(100%)", opacity: 0.4 } : {}),
      }}
    >
      <CircleMesh
        key={badgeSeed}
        targetCircleSize={8}
        seed={badgeConfig.seed}
        mutingIntensity={0}
        animate={false}
        restrictedColors={badgeConfig.colors}
      />
    </span>
  );
}

export function WorkflowConnectorButton({
  workflowId,
  spaceId,
  workflowName,
  workflowBadgeSeed,
}: Props) {
  const canCreateApp = useHasAccess("tenant:connected_app_create");
  const canWrite = useHasAccess("space:write", spaceId);
  const { branchId } = useCurrentUrl(routes.space.workflow);
  const exposeAsConnector = useExposeWorkflowAsConnector({
    workflowId,
    spaceId,
    workflowName,
  });
  const [currentApp] = useQuery(
    queries.connectedAppForWorkflow({ workflowId }),
    { enabled: canCreateApp }
  );
  const hasExposedRoutes = useHasExposedConnectorRoutes(
    workflowId,
    branchId || undefined,
    canCreateApp && canWrite && !currentApp
  );
  const isPending = !currentApp;

  // Briefly nudge (pop + tooltip) when the workflow exposes connector links but
  // has no backing app yet, so the links can’t be called — then auto-dismiss so
  // the tooltip isn’t pinned open for the whole partial-setup state.
  const shouldHint = !currentApp && hasExposedRoutes;
  const hintPlayedRef = useRef(false);
  const [hintVisible, setHintVisible] = useState(false);
  useEffect(() => {
    if (shouldHint && !hintPlayedRef.current) {
      hintPlayedRef.current = true;
      playPop();
      setHintVisible(true);
    } else if (!shouldHint) {
      hintPlayedRef.current = false;
      setHintVisible(false);
    }
  }, [shouldHint]);

  useEffect(() => {
    if (!hintVisible) return;
    const hide = setTimeout(() => setHintVisible(false), 5000);
    return () => clearTimeout(hide);
  }, [hintVisible]);

  if (!canCreateApp) return null;
  if (!currentApp && !canWrite) return null;

  const handleClick = () => {
    if (currentApp) {
      navigate(routes.modal.connector, { id: currentApp.id });
      return;
    }
    const newAppId = exposeAsConnector();
    if (newAppId) navigate(routes.modal.connector, { id: newAppId });
  };

  const tooltip = currentApp
    ? "This workflow is a connector"
    : "Use this workflow as a connector";

  return (
    <Tooltip content={tooltip} align="center" forceOpen={hintVisible}>
      <PillButton aria-label={tooltip} onClick={handleClick}>
        <IconSlot>
          <WorkflowAppBadge
            id={currentApp?.backingWorkflowId ?? workflowId}
            badgeSeed={workflowBadgeSeed}
            muted={isPending}
          />
        </IconSlot>
        <FlowLine
          style={
            {
              "--wf-app-flow-opacity": isPending ? 0.4 : 1,
            } as React.CSSProperties
          }
        />
        <IconSlot>
          <Icon
            name="api-key-custom"
            size={16}
            style={isPending ? { opacity: 0.4 } : undefined}
          />
        </IconSlot>
      </PillButton>
    </Tooltip>
  );
}
